/**
 * D-EZ-101 — rulebook citations, shown only when the player asks for them.
 *
 * `<Cite>` renders its children only while "Show rulebook citations" is ON
 * (see `lib/prefs.ts`). Wrap a rule code, a sheet reference or an internal
 * engine note in it and the default screen reads as plain racing; Howard's
 * checking view still sees every reference exactly where it always was.
 *
 * `<ErrorNote>` is the plain-language error line: a sentence a player can act
 * on, with the raw technical text available behind the same switch.
 *
 * `<CitationsSwitch>` is the checkbox itself, for any settings area.
 */

import type { ReactNode } from 'react';
import { setShowCitations, useShowCitations } from '../lib/prefs.ts';

export function Cite({
  children,
  className,
  as = 'span',
}: {
  children: ReactNode;
  className?: string;
  as?: 'span' | 'p' | 'small' | 'cite' | 'div';
}) {
  const on = useShowCitations();
  if (!on) return null;
  const El = as;
  return <El className={className}>{children}</El>;
}

/** True when citations are on — for the odd string that has to be built, not wrapped. */
export { useShowCitations };

export interface FriendlyError {
  message: string;
  detail: string;
}

export function ErrorNote({ error, className = 'err' }: { error: FriendlyError | string | null; className?: string }) {
  const on = useShowCitations();
  if (!error) return null;
  if (typeof error === 'string') return <p className={className} role="alert">{error}</p>;
  return (
    <p className={className} role="alert">
      {error.message}
      {on && error.detail && (
        <>
          {' '}
          <small className="err__detail">({error.detail})</small>
        </>
      )}
    </p>
  );
}

export function CitationsSwitch() {
  const on = useShowCitations();
  return (
    <label className="prefswitch">
      <input type="checkbox" checked={on} onChange={(e) => setShowCitations(e.target.checked)} />
      <span>
        <b>Show rulebook citations</b>
        <small>
          Adds the sheet and rule references (and the race seed) beside results. Off reads as plain racing.
        </small>
      </span>
    </label>
  );
}
